import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { StatusInscricao } from '../types/inscricao';
import { StatusPagamento } from '../types/pagamento';

export { StatusInscricao };

export const FINALIZAR_PAGAMENTO_INSCRICAO_MUTATION = gql`
  mutation FinalizarPagamentoInscricao($pagamentoInscricaoId: ID!, $input: FinalizarPagamentoInput!) {
    finalizarPagamentoInscricao(pagamentoInscricaoId: $pagamentoInscricaoId, input: $input) {
      id
      status
      dataPagamento
      meioPagamento
      valor
      valorSemDesconto
      descontoAplicado
      cupomAplicado {
        codigo
        tipo
        valor
        quantidadeDisponivel
        status
        criadoEm
        atualizadoEm
      }
      inscricoes {
        id
        status
      }
      pixInfo {
        invoiceId
        qrcodeImageUrl
        qrcodeText
        status
      }
    }
  }
`;

export type MeioPagamento = 'CARTAO' | 'PIX';

export interface PagamentoPixInfo {
  invoiceId: string | null;
  qrcodeImageUrl: string | null;
  qrcodeText: string | null;
  status: string | null;
}

export interface FinalizarPagamentoResult {
  id: string;
  status: StatusPagamento;
  meioPagamento: MeioPagamento | null;
  pixInfo: PagamentoPixInfo | null;
}

export interface EnderecoCobrancaInput {
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  pais: string;
  complemento?: string;
}

export interface DadosCartaoInput {
  numero: string;
  nomeImpresso: string;
  validadeMes: number;
  validadeAno: number;
  cvv: string;
}

export async function finalizarPagamentoInscricaoService(params: {
  pagamentoInscricaoId: string;
  input: {
    meioPagamento: MeioPagamento;
    enderecoCobranca: EnderecoCobrancaInput;
    dadosCartao?: DadosCartaoInput;
  };
  /** Quando true, o erro NÃO é despachado pelo handler global (o chamador trata localmente). */
  suppressGlobalError?: boolean;
}): Promise<FinalizarPagamentoResult | undefined> {
  const { pagamentoInscricaoId, input, suppressGlobalError } = params;
  const { data } = await apolloClient.mutate<{
    finalizarPagamentoInscricao: FinalizarPagamentoResult;
  }>({
    mutation: FINALIZAR_PAGAMENTO_INSCRICAO_MUTATION,
    variables: { pagamentoInscricaoId, input },
    context: { suppressGlobalError: !!suppressGlobalError },
  });
  return data?.finalizarPagamentoInscricao;
}

export const CRIAR_PAGAMENTO_INSCRICAO = gql`
  mutation CriarPagamentoInscricaoInscricaoTorneio($input: PagamentoInscricaoTorneioInput!) {
    criarPagamentoInscricaoInscricaoTorneio(input: $input) {
      id
      codigoTransacao
      criadoEm
      dataPagamento
      dataVencimento
      cupomAplicado {
        codigo
        valor
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
      descontoAplicado
      meioPagamento
      reciboUrl
      status
      valor
      valorSemDesconto
      invoiceId
      motivoCancelamento
      dataCancelamento
      inscricoes {
        status
        id
        observacao
        dataInscricao
        dataCancelamento
        motivoCancelamento
        categoriaTorneio {
          id
          nome
          precoInscricao
          aplicarDescontoTorneio
          torneio {
            id
            nome
            dataInicio
            dataFim
            bannerUrl
            avatarUrl
            ligaId
          }
        }
        equipe {
          id
          nome
          cidade
          atletas {
            id
            cpf
            telefone
            cidade { id nome uf }
            usuario { id email nome imagemUrl username }
          }
        }
      }
    }
  }
`;

export interface PagamentoInscricaoTorneioInput {
  inscricaoIds: string[];
  valorSemDesconto?: number;
  descontoAplicado?: number;
  // Alguns backends podem esperar 'valor' em vez de 'valorComDesconto'.
  // Mantemos o nome pedido no requisito e mapeamos no serviÃ§o se necessÃ¡rio.
  valorComDesconto?: number;
}

export interface ValoresInscricao {
  valorSemDesconto: number;
  descontoAplicado: number;
  valorComDesconto: number;
}

export interface PagamentoInscricao {
  id: string;
  valor?: number | null;
  valorSemDesconto?: number | null;
  descontoAplicado?: number | null;
  status: string;
  origemConfirmacao?: 'WEBHOOK' | 'MANUAL' | string | null;
  reciboUrl?: string | null;
  meioPagamento?: string | null;
  dataPagamento?: string | null;
  criadoEm?: string | null;
  dataVencimento?: string | null;
  codigoTransacao?: string | null;
  invoiceId?: string | null;
  motivoCancelamento?: string | null;
  dataCancelamento?: string | null;
  cupomAplicado?: {
    id?: string;
    codigo: string;
    valor: number;
    quantidadeDisponivel: number;
    tipo: 'FIXO' | 'PORCENTAGEM';
    status: 'ATIVO' | 'INATIVO';
    criadoEm: string;
    atualizadoEm: string;
  } | null;
  pedidosReembolso?: { id: string; status: string }[];
  inscricoes: {
    status: string;
    compartilharInformacoes: boolean;
    id: string;
    // Opcional: só as duas consultas de Recebimentos selecionam `isento`. As
    // demais que produzem este tipo não pedem o campo, e declará-lo obrigatório
    // quebraria em compilação toda fixture literal dessas suítes — mesma tensão
    // já registrada no CLAUDE.md da raiz para `ResumoFinanceiro`.
    isento?: boolean | null;
    observacao?: string | null;
    dataInscricao?: string | null;
    dataCancelamento?: string | null;
    motivoCancelamento?: string | null;
    categoriaTorneio: {
      id: string;
      nome: string;
      precoInscricao?: number | null;
      aplicarDescontoTorneio?: boolean | null;
      torneio: {
        id: string;
        nome: string;
        dataInicio?: string | null;
        dataFim?: string | null;
        bannerUrl?: string | null;
        avatarUrl?: string | null;
        ligaId?: string | null;
        local?: {
          id: string;
          nome: string;
          endereco?: {
            cidade: { id: string; nome: string; uf: string };
            logradouro: string;
            numero: string;
            complemento?: string | null;
          };
        };
      };
    };
    equipe: {
      id: string;
      nome: string;
      cidade?: string | null;
      atletas: {
        id: string;
        cpf?: string | null;
        telefone?: string | null;
        cidade?: { id: string; nome: string; uf: string } | null;
        usuario: { id: string; email: string; nome: string; imagemUrl?: string | null; username?: string | null };
      }[];
    };
  }[];
}

export interface CriarPagamentoInscricaoData {
  criarPagamentoInscricaoInscricaoTorneio: PagamentoInscricao;
}

export interface CriarPagamentoInscricaoVars {
  input: PagamentoInscricaoTorneioInput;
}

export const criarPagamentoInscricao = async (
  inscricaoIds: string[],
  valores?: ValoresInscricao,
): Promise<PagamentoInscricao | undefined> => {
  const input: PagamentoInscricaoTorneioInput = { inscricaoIds };
  if (valores) {
    input.valorSemDesconto = valores.valorSemDesconto;
    input.descontoAplicado = valores.descontoAplicado;
    // Por ora enviamos como 'valorComDesconto'. Se o backend esperar 'valor', ajuste aqui.
    input.valorComDesconto = valores.valorComDesconto;
  }
  const { data } = await apolloClient.mutate<CriarPagamentoInscricaoData, CriarPagamentoInscricaoVars>({
    mutation: CRIAR_PAGAMENTO_INSCRICAO,
    variables: { input },
  });
  return data?.criarPagamentoInscricaoInscricaoTorneio;
};

export const PAGAMENTOS_ATLETA = gql`
  query PagamentosInscricaoPorAtleta($atletaId: ID!, $page: Int = 0, $size: Int = 10) {
    pagamentosInscricaoPorAtleta(atletaId: $atletaId, page: $page, size: $size) {
      page
      size
      totalItems
      totalPages
      items {
        id
        codigoTransacao
        criadoEm
        dataPagamento
        cupomAplicado {
          codigo
          valor
          quantidadeDisponivel
          tipo
          status
          criadoEm
          atualizadoEm
        }
        descontoAplicado
        meioPagamento
        reciboUrl
        status
        valor
        valorSemDesconto
        pedidosReembolso { id status }
        inscricoes {
          id
          categoriaTorneio {
            id
            nome
            aplicarDescontoTorneio
            torneio { id nome ligaId }
          }
        }
      }
    }
  }
`;

export interface PagamentoInscricaoConnection {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  items: PagamentoInscricao[];
}

export interface PagamentosAtletaData {
  pagamentosInscricaoPorAtleta: PagamentoInscricaoConnection;
}

export interface PagamentosAtletaVars {
  atletaId: string;
  page?: number;
  size?: number;
}

export const listarPagamentosAtleta = async (
  atletaId: string,
  page: number = 0,
  size: number = 10,
): Promise<PagamentoInscricaoConnection> => {
  const { data } = await apolloClient.query<PagamentosAtletaData, PagamentosAtletaVars>({
    query: PAGAMENTOS_ATLETA,
    variables: { atletaId, page, size },
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosInscricaoPorAtleta;
};

export const PAGAMENTO_POR_ID = gql`
  query PagamentoInscricaoPorId($id: ID!) {
    pagamentoInscricaoPorId(id: $id) {
      id
      codigoTransacao
      criadoEm
      dataPagamento
      dataVencimento
      cupomAplicado {
        codigo
        valor
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
      descontoAplicado
      meioPagamento
      reciboUrl
      status
      valor
      valorSemDesconto
      invoiceId
      motivoCancelamento
      dataCancelamento
      pedidosReembolso { id status }
      inscricoes {
        status
        compartilharInformacoes
        id
        observacao
        dataInscricao
        dataCancelamento
        motivoCancelamento
        categoriaTorneio {
          id
          nome
          precoInscricao
          aplicarDescontoTorneio
          torneio {
            id
            nome
            dataInicio
            dataFim
            bannerUrl
            avatarUrl
            ligaId
          }
        }
        equipe {
          id
          nome
          cidade
          atletas {
            id
            cpf
            telefone
            cidade { id nome uf }
            usuario { id email nome imagemUrl username }
          }
        }
      }
    }
  }
`;

export interface PagamentoPorIdData {
  pagamentoInscricaoPorId: PagamentoInscricao;
}

export interface PagamentoPorIdVars {
  id: string;
}

export const pagamentoPorId = async (id: string) => {
  const { data } = await apolloClient.query<PagamentoPorIdData, PagamentoPorIdVars>({
    query: PAGAMENTO_POR_ID,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
  return data.pagamentoInscricaoPorId;
};

export const PAGAMENTOS_POR_CATEGORIA_TORNEIO = gql`
  query PagamentosPorCategoriaTorneio($categoriaTorneioId: ID!, $atletaId: ID!) {
    pagamentosPorCategoriaTorneio(
      categoriaTorneioId: $categoriaTorneioId
      atletaId: $atletaId
    ) {
      id
      codigoTransacao
      criadoEm
      dataPagamento
      cupomAplicado {
        codigo
        valor
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
      descontoAplicado
      meioPagamento
      reciboUrl
      status
      valor
      valorSemDesconto
    }
  }
`;

export interface PagamentosPorCategoriaTorneioData {
  pagamentosPorCategoriaTorneio: { id: string }[];
}

export interface PagamentosPorCategoriaTorneioVars {
  categoriaTorneioId: string;
  atletaId: string;
}

export const listarPagamentosPorCategoriaTorneio = async (
  categoriaTorneioId: string,
  atletaId: string,
) => {
  const { data } = await apolloClient.query<
    PagamentosPorCategoriaTorneioData,
    PagamentosPorCategoriaTorneioVars
  >({
    query: PAGAMENTOS_POR_CATEGORIA_TORNEIO,
    variables: { categoriaTorneioId, atletaId },
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosPorCategoriaTorneio;
};

// financeiro-dashboard-plataforma Phase 5 (AC-13, plano AC-A2) -- $ligaId/$inicio/$fim
// adicionados de forma ADITIVA (schema.graphqls:226 ja aceita os 3; confirmado por leitura
// direta nesta fase). Nenhum argumento/campo existente foi removido ou renomeado --
// TODOS os consumidores pre-existentes continuam funcionando sem alteracao, porque nenhum
// deles inclui os 3 novos no seu objeto `variables` e eles resolvem para `undefined`.
// NAO enumere esses consumidores aqui: um `grep` por substring de
// PAGAMENTOS_INSCRICAO_FILTRADOS casa tambem PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA e
// PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS, que sao documentos DISTINTOS -- duas tentativas de
// listar deram errado nesta mesma fase por causa disso. A forma real: 1 consumidor direto
// de componente (TournamentFinanceiroRecebimentosTab.tsx) + 3 helpers deste mesmo arquivo
// (buscarPagamentosInscricaoFiltrados, listarPagamentos, listarPagamentosPorAtleta), sendo
// que listarPagamentos sozinho e chamado por 7 pages. Qualquer lista literal nasce velha.
export const PAGAMENTOS_INSCRICAO_FILTRADOS = gql`
  query PagamentosInscricaoFiltrados(
    $atletaId: ID
    $categoriaTorneioId: ID
    $torneioId: ID
    $ligaId: ID
    $status: StatusPagamento
    $inicio: Date
    $fim: Date
    $nomeAtleta: String
    $sortBy: OrdenacaoPagamento
    $direction: DirecaoOrdenacao
    $page: Int!
    $size: Int!
  ) {
    pagamentosInscricaoFiltrados(
      atletaId: $atletaId
      categoriaTorneioId: $categoriaTorneioId
      torneioId: $torneioId
      ligaId: $ligaId
      page: $page
      size: $size
      status: $status
      inicio: $inicio
      fim: $fim
      nomeAtleta: $nomeAtleta
      sortBy: $sortBy
      direction: $direction
    ) {
      page
      size
      totalItems
      totalPages
      items {
        id
        codigoTransacao
        criadoEm
        dataPagamento
        cupomAplicado {
          codigo
          valor
          quantidadeDisponivel
          tipo
          status
          criadoEm
          atualizadoEm
        }
        descontoAplicado
        meioPagamento
        reciboUrl
        status
        origemConfirmacao
        valor
        valorSemDesconto
        pedidosReembolso { id status }
        inscricoes {
          id
          isento
          dataInscricao
          dataCancelamento
          motivoCancelamento
          status
          equipe {
            id
            nome
            atletas {
              id
              usuario {
                id
                imagemUrl
                nome
                username
              }
              cpf
              telefone
              cidade {
                id
                nome
                uf
              }
            }
          }
          categoriaTorneio {
            id
            nome
            precoInscricao
            aplicarDescontoTorneio
            torneio {
              id
              nome
              dataFim
              dataInicio
              avatarUrl
              bannerUrl
              ligaId
              liga {
                id
                nome
              }
            }
          }
        }
      }
    }
  }
`;

export interface PagamentosInscricaoFiltradosData {
  pagamentosInscricaoFiltrados: {
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
    items: PagamentoInscricao[];
  };
}


export type OrdenacaoPagamento = 'DATA' | 'VALOR' | 'STATUS';
export type DirecaoOrdenacao = 'ASC' | 'DESC';

export interface PagamentosInscricaoFiltradosVars {
  atletaId?: string;
  categoriaTorneioId?: string;
  torneioId?: string;
  ligaId?: string;
  status?: StatusPagamento;
  inicio?: string;
  fim?: string;
  nomeAtleta?: string;
  sortBy?: OrdenacaoPagamento;
  direction?: DirecaoOrdenacao;
  page: number;
  size: number;
}

/**
 * Busca pagamentos de inscriÃ§Ã£o com filtros opcionais.
 *
 * @param atletaId - ID do atleta a filtrar.
 * @param categoriaTorneioId - ID da categoria do torneio a filtrar.
 * @param status - Status do pagamento de inscriÃ§Ã£o.
 * @param page - NÃºmero da pÃ¡gina (obrigatÃ³rio).
 * @param size - Quantidade de itens por pÃ¡gina (obrigatÃ³rio).
 * @returns Objeto de paginaÃ§Ã£o contendo os pagamentos encontrados.
 */
export const buscarPagamentosInscricaoFiltrados = async (
  atletaId: string | undefined,
  categoriaTorneioId: string | undefined,
  status: StatusPagamento | undefined,
  page: number,
  size: number,
) => {
  const { data } = await apolloClient.query<
    PagamentosInscricaoFiltradosData,
    PagamentosInscricaoFiltradosVars
  >({
    query: PAGAMENTOS_INSCRICAO_FILTRADOS,
    variables: { atletaId, categoriaTorneioId, status, page, size },
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosInscricaoFiltrados;
};

// --- Novas funÃ§Ãµes padronizadas de listagem ---
// MantÃªm a consulta unificada do backend (pagamentosInscricaoFiltrados)
// e retornam o objeto de conexÃ£o completo.

export const listarPagamentos = async (params?: {
  page?: number;
  size?: number;
  status?: StatusPagamento;
  atletaId?: string | null;
  categoriaId?: string | null;
  torneioId?: string | null;
}) => {
  const page = params?.page ?? 0;
  const size = params?.size ?? 50;
  const status = params?.status;
  const atletaId = params?.atletaId ?? undefined;
  const categoriaTorneioId = params?.categoriaId ?? undefined;
  const torneioId = params?.torneioId ?? undefined;
  const variables: PagamentosInscricaoFiltradosVars = { atletaId, categoriaTorneioId, status, page, size };
  if (torneioId) {
    variables.torneioId = torneioId;
  }
  const { data } = await apolloClient.query<
    PagamentosInscricaoFiltradosData,
    PagamentosInscricaoFiltradosVars
  >({
    query: PAGAMENTOS_INSCRICAO_FILTRADOS,
    variables,
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosInscricaoFiltrados;
};

export const listarPagamentosPorAtleta = async (
  atletaId: string,
  params?: { page?: number; size?: number; status?: StatusPagamento; categoriaId?: string | null; torneioId?: string | null },
) => {
  const page = params?.page ?? 0;
  const size = params?.size ?? 50;
  const status = params?.status;
  const categoriaTorneioId = params?.categoriaId ?? undefined;
  const torneioId = params?.torneioId ?? undefined;
  const variables: PagamentosInscricaoFiltradosVars = { atletaId, categoriaTorneioId, status, page, size };
  if (torneioId) {
    variables.torneioId = torneioId;
  }
  const { data } = await apolloClient.query<
    PagamentosInscricaoFiltradosData,
    PagamentosInscricaoFiltradosVars
  >({
    query: PAGAMENTOS_INSCRICAO_FILTRADOS,
    variables,
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosInscricaoFiltrados;
};

export const PAGAMENTOS_LIGA = gql`
  query PagamentosInscricaoPorLiga($ligaId: ID!, $page: Int = 0, $size: Int = 10) {
    pagamentosInscricaoPorLiga(ligaId: $ligaId, page: $page, size: $size) {
      page
      size
      totalItems
      totalPages
      items {
        id
        codigoTransacao
        criadoEm
        dataPagamento
        cupomAplicado {
          codigo
          valor
          quantidadeDisponivel
          tipo
          status
          criadoEm
          atualizadoEm
        }
        descontoAplicado
        meioPagamento
        reciboUrl
        status
        valor
        valorSemDesconto
        pedidosReembolso { id status }
        inscricoes {
          id
          isento
          categoriaTorneio {
            id
            nome
            aplicarDescontoTorneio
            torneio { id nome ligaId }
          }
          equipe {
            id
            nome
            atletas {
              id
              usuario { id nome }
            }
          }
        }
      }
    }
  }
`;

export interface PagamentosLigaData {
  pagamentosInscricaoPorLiga: PagamentoInscricaoConnection;
}

export interface PagamentosLigaVars {
  ligaId: string;
  page?: number;
  size?: number;
}

export const listarPagamentosPorLiga = async (
  ligaId: string,
  page: number = 0,
  size: number = 10,
): Promise<PagamentoInscricaoConnection> => {
  const { data } = await apolloClient.query<PagamentosLigaData, PagamentosLigaVars>({
    query: PAGAMENTOS_LIGA,
    variables: { ligaId, page, size },
    fetchPolicy: 'no-cache',
  });
  return data.pagamentosInscricaoPorLiga;
};

export const APLICAR_CUPOM = gql`
  mutation AplicarCupom($idCupom: ID!, $idPagamento: ID!) {
    aplicarCupom(idCupom: $idCupom, idPagamento: $idPagamento) {
      id
      codigoTransacao
      criadoEm
      dataPagamento
      cupomAplicado {
        codigo
        valor
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
      descontoAplicado
      meioPagamento
      reciboUrl
      status
      valor
      valorSemDesconto
    }
  }
`;

export const RETIRAR_CUPOM = gql`
  mutation RetirarCupom($idPagamento: ID!) {
    retirarCupom(idPagamento: $idPagamento) {
      codigoTransacao
      criadoEm
      dataPagamento
      descontoAplicado
      id
      meioPagamento
      status
      reciboUrl
      valor
      valorSemDesconto
      cupomAplicado {
        valor
        codigo
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
    }
  }
`;

interface AplicarCupomData {
  aplicarCupom: PagamentoInscricao;
}

interface AplicarCupomVars {
  idCupom: string;
  idPagamento: string;
}

export const aplicarCupomPagamento = async (
  idCupom: string,
  idPagamento: string,
): Promise<PagamentoInscricao | undefined> => {
  if (!idCupom || !idPagamento) return undefined;
  const { data } = await apolloClient.mutate<AplicarCupomData, AplicarCupomVars>({
    mutation: APLICAR_CUPOM,
    variables: { idCupom, idPagamento },
  });
  return data?.aplicarCupom;
};

interface RetirarCupomData {
  retirarCupom: PagamentoInscricao;
}

interface RetirarCupomVars {
  idPagamento: string;
}

export const retirarCupomPagamento = async (
  idPagamento: string,
): Promise<PagamentoInscricao | undefined> => {
  if (!idPagamento) return undefined;
  const { data } = await apolloClient.mutate<RetirarCupomData, RetirarCupomVars>({
    mutation: RETIRAR_CUPOM,
    variables: { idPagamento },
  });
  return data?.retirarCupom;
};

export const CANCELAR_PAGAMENTO_INSCRICAO = gql`
  mutation CancelarPagamentoInscricao($id: ID!, $motivo: String) {
    cancelarPagamentoInscricao(id: $id, motivo: $motivo) {
      pagamento {
        id
        status
        dataCancelamento
        motivoCancelamento
      }
      inscricoesCanceladas {
        id
        status
        dataCancelamento
        motivoCancelamento
      }
      inscricoesIgnoradas {
        id
        status
      }
    }
  }
`;

export type StatusPagamentoInscricao = "PENDENTE" | "AGUARDANDO" | "CANCELADO" | "PAGO" | "FALHA"; // ajuste para o seu enum real
export type StatusInscricaoTorneio = StatusInscricao;

export interface PagamentoInscricaoDTO {
  id: string;
  status: StatusPagamentoInscricao;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
}

export interface InscricaoTorneioDTO {
  id: string;
  status: StatusInscricaoTorneio;
  dataCancelamento?: string | null;
  motivoCancelamento?: string | null;
}

export interface CancelarPagamentoInscricaoPayload {
  pagamento: PagamentoInscricaoDTO;
  inscricoesCanceladas: InscricaoTorneioDTO[];
  inscricoesIgnoradas: InscricaoTorneioDTO[];
}

/**
 * Cancela um pagamento e, em seguida, cancela todas as inscrições ligadas a ele que estiverem PENDENTES.
 * @param id - ID do PagamentoInscricao
 * @param motivo - (opcional) motivo do cancelamento
 * @returns payload com pagamento atualizado, inscrições canceladas e ignoradas
 */
export async function cancelarPagamentoEInscricoes(
  id: string,
  motivo?: string
): Promise<CancelarPagamentoInscricaoPayload> {
  const { data, errors } = await apolloClient.mutate<{
    cancelarPagamentoInscricao: CancelarPagamentoInscricaoPayload;
  }>({
    mutation: CANCELAR_PAGAMENTO_INSCRICAO,
    variables: { id, motivo },
    // Se você usa cache Apollo e quiser garantir atualização:
    fetchPolicy: "no-cache",
  });

  if (errors && errors.length) {
    // Propaga o primeiro erro GraphQL (ajuste se tiver um handler global)
    throw new Error(errors[0].message);
  }

  if (!data?.cancelarPagamentoInscricao) {
    throw new Error("Resposta inválida ao cancelar pagamento/inscrições.");
  }

  return data.cancelarPagamentoInscricao;
}

// --------------------------------------------------------------------------------
// Atualizar pagamento de inscrição
// --------------------------------------------------------------------------------

export interface AtualizarPagamentoInscricaoInput {
  status?: string | null;
  dataVencimento?: string | null;
  motivoCancelamento?: string | null;
}

export const ATUALIZAR_PAGAMENTO_INSCRICAO = gql`
  mutation AtualizarPagamentoInscricao($id: ID!, $input: PagamentoInscricaoInput!) {
    atualizarPagamentoInscricao(id: $id, input: $input) {
      id
      status
      valor
      valorSemDesconto
      descontoAplicado
      dataVencimento
      motivoCancelamento
      dataCancelamento
      invoiceId
      reciboUrl
      criadoEm
      cupomAplicado {
        codigo
        valor
        quantidadeDisponivel
        tipo
        status
        criadoEm
        atualizadoEm
      }
      meioPagamento
      dataPagamento
      codigoTransacao
    }
  }
`;

// --------------------------------------------------------------------------------
// Deletar pagamento de inscrição
// --------------------------------------------------------------------------------

export const DELETAR_PAGAMENTO_INSCRICAO = gql`
  mutation DeletarPagamentoInscricao($id: ID!) {
    deletarPagamentoInscricao(id: $id)
  }
`;

export const deletarPagamentoInscricao = async (id: string): Promise<boolean> => {
  const { data } = await apolloClient.mutate<
    { deletarPagamentoInscricao: boolean },
    { id: string }
  >({
    mutation: DELETAR_PAGAMENTO_INSCRICAO,
    variables: { id },
    fetchPolicy: "no-cache",
  });

  if (!data?.deletarPagamentoInscricao) {
    throw new Error('Não foi possível deletar o pagamento.');
  }

  return data.deletarPagamentoInscricao;
};

export const atualizarPagamentoInscricao = async (
  id: string,
  input: AtualizarPagamentoInscricaoInput,
): Promise<PagamentoInscricao> => {
  const { data } = await apolloClient.mutate<
    { atualizarPagamentoInscricao: PagamentoInscricao },
    { id: string; input: AtualizarPagamentoInscricaoInput }
  >({
    mutation: ATUALIZAR_PAGAMENTO_INSCRICAO,
    variables: { id, input },
    fetchPolicy: "no-cache",
  });

  if (!data?.atualizarPagamentoInscricao) {
    throw new Error('Não foi possível atualizar o pagamento.');
  }

  return data.atualizarPagamentoInscricao;
};
